import * as THREE from "three";
import {Box3} from "three";
import * as Globals from "../../globals";
import {CutOut} from "./cutOut";
import {setObjectLayer} from "../../utils/layerUtils";
import {createGroup, findChildByName, getLengthByAxis} from "../../utils/threeUtils";
import {getWallGroupParent} from "../../utils/configuratorUtils";
import {raehmSchwelleMaterial, windowMaterial} from "../../constants/materialConstants";
import {recalculateBuildingParameters} from "../../calculators/buildingParameters/parameterCalculator";
import {toFixedFloat} from "../../utils/utils";
import {cutOutsGroupName} from "../../constants/nameConstants";
import {getVolume} from "../../utils/volumeUtils";

export class Window extends CutOut {
    create(wallGroup = null, wallMeshName = null) {
        if (!this.placeable) {
            return false;
        }

        let wallMesh = wallGroup === null ? this.getMeshToCut() : findChildByName(wallGroup, wallMeshName);
        if (wallMesh === null) {
            return false;
        }

        wallGroup = getWallGroupParent(wallMesh); //Get the wallGroup the element is currently hovering on

        let adjustOriginalWall = false;
        let originalWallGroup = null;
        //Wenn der CutOut recreated ist muss geprüft werden, ob die Wand die gleiche wie vorher ist sonst muss die alte Wand auch nachher angepasst werden
        if (this.recreated) {
            originalWallGroup = CutOut.clearOldCutOut(this.controlObject);
            adjustOriginalWall = wallGroup !== originalWallGroup;
        }

        if (wallGroup === null) {
            return false;
        }


        let bounds = new Box3().setFromObject(wallMesh);
        let wallElementHeight = toFixedFloat(getLengthByAxis(bounds, "y")); //Get group height

        //TODO: Temp fix für falsch gerenderte Durchbrüche
        //Bodenständige Fenster (volle Elementhöhe) benötigen keinen Brüstungsriegel. In diesem Fall dient die Schwelle als Riegel
        this.bruestungsRiegelHeight = toFixedFloat(Math.min(wallElementHeight - this.height, Globals.defaultBruestungsRiegelHeight));
        let addBruestungsriegel = this.bruestungsRiegelHeight !== 0;

        if (addBruestungsriegel) {
            this.height += this.bruestungsRiegelHeight; //Loch schneiden mit Fensterhöhe + Querbalken
        }

        this.bottomOffset = wallElementHeight - this.height + Globals.defaultSchwelleHeight; //Element muss immer an Unterkante des Rähms abschließen
        let cutOutsGroup = findChildByName(wallGroup, cutOutsGroupName);
        this.cutOutGroup = createGroup("windowGroup", cutOutsGroup); //TODO: Check If not null
        this.cutMesh = this.createCuttingMesh();
        this.cutWallSegment(wallGroup);

        if (addBruestungsriegel) {
            this.height -= this.bruestungsRiegelHeight; //Querbalken wieder rausrechnen für FillMesh
        }

        let windowMesh = this.createFillMesh();

        if (addBruestungsriegel) {
            windowMesh.translateY(-(this.bruestungsRiegelHeight / 2)); //Verschiebung für Brüstungsriegel
            this.createBruestungsriegel(); //Brüstungsriegel einfügen
        }


        wallGroup.updateMatrix();
        for (let i = 0; i < wallGroup.children.length; i++) {
            wallGroup.children[i].updateMatrix();
        }

        this.createControlObject(wallGroup, wallMesh.name);


        if (adjustOriginalWall) {
            CutOut.adjustWall(originalWallGroup);
        }

        CutOut.adjustWall(wallGroup);

        recalculateBuildingParameters().then();

        return true;
    }

    createCuttingMesh() {
        let cuttingMesh = super.createCuttingMesh();
        cuttingMesh.name = "windowCutMesh";
        return cuttingMesh;
    }

    createFillMesh() {
        let windowMesh = new THREE.Mesh(new THREE.BoxGeometry(this.width, this.height, Globals.HBEWallDefaultThickness / 4), windowMaterial);
        setObjectLayer(windowMesh, Globals.layers.wall);
        windowMesh.name = "windowMesh";
        windowMesh.updateMatrix();
        this.cutOutGroup.addWithUuid(windowMesh);
        //Position des Fillmeshes muss durch geänderte Größe verschoben werden
        windowMesh.position.set(this.cutMesh.position.x, this.cutMesh.position.y, this.cutMesh.position.z); //Grundposition des Fenster > Bei Bodenständigen Fenstern
        windowMesh.rotateX(Math.PI / 2);
        windowMesh.updateMatrix();
        return windowMesh;
    }

    createBruestungsriegel() {
        let bruestungsRiegel = new THREE.Mesh(new THREE.BoxGeometry(this.width, this.bruestungsRiegelHeight, Globals.HBEWallDefaultThickness), raehmSchwelleMaterial);
        setObjectLayer(bruestungsRiegel, Globals.layers.wall);
        bruestungsRiegel.name = "querbalkenMesh";
        this.cutOutGroup.addWithUuid(bruestungsRiegel);
        bruestungsRiegel.position.set(this.cutMesh.position.x, this.cutMesh.position.y, this.cutMesh.position.z + this.height / 2);
        bruestungsRiegel.rotateX(Math.PI / 2);
        bruestungsRiegel.updateMatrix();
        bruestungsRiegel.userData = {
            width: this.width,
            depth: Globals.HBEWallDefaultThickness,
            height: this.bruestungsRiegelHeight,
            originalVolume: getVolume(bruestungsRiegel.geometry)
        };
    }

    setControlObjectUserData(wallMeshName, sceneMatrix) {
        super.setControlObjectUserData(wallMeshName, sceneMatrix);
        this.controlObject.userData.type = "window";
    }
}
